import { useCallback, useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useDisputeTransactionsStore } from '@lib/store';
import { cn } from '@shared/utils';
import type { ChangeCardNameRequest } from '@shared/api';
import { TransactionForm, TransactionTile } from './widgets';
import { Button, Input, Modal, PageLayout } from '@shared/widgets';
import bgImg from '@assets/images/disputedCharges/form-bg-mobile.png';
import formBgImg from '@assets/images/disputedCharges/form-bg.png';

export interface IForm {
  firstName?: string;
  lastName?: string;
  cardNumber?: number | string;
  transactions?: TransactionForm[];
}

function encodeRequest(value: ChangeCardNameRequest & IForm) {
  const result = { ...value };
  if (result.firstName || result.lastName) {
    result.cardholderName = result.firstName;
    if (result.lastName) {
      result.cardholderName += ' ' + result.lastName;
    }
    delete result.firstName;
    delete result.lastName;
  }
  if (result.transactions) {
    result.descriptionOfTransactions = [...result.transactions].map((item) => {
      const result = { ...item };
      if (result.formCurrency !== 'Other') result.currency = result.formCurrency;
      if (result.formTypeOfDispute !== 'Other') result.typeOfDispute = result.formTypeOfDispute;
      delete result.formCurrency;
      delete result.formTypeOfDispute;
      return result;
    });
    delete result.transactions;
  }
  return result;
}

export const DisputedChargesPage = () => {
  const formControl = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      cardNumber: undefined,
      transactions: [],
    } as IForm,
  });

  const fieldArray = useFieldArray({
    control: formControl.control,
    name: 'transactions',
  });

  const disputeTransactionsStore = useDisputeTransactionsStore();

  const [addingTransaction, setAddingTransaction] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [editingTransactionIndex, setEditingTransactionIndex] = useState<number | null>(null);

  const transactions = formControl.watch('transactions');

  useEffect(() => {
    if (disputeTransactionsStore.success) setConfirmationVisible(true);
  }, [disputeTransactionsStore.success]);

  const handleSaveTransaction = useCallback(
    (data: TransactionForm) => {
      if (editingTransactionIndex !== null) {
        fieldArray.update(editingTransactionIndex, {
          ...data,
          index: editingTransactionIndex,
        });
        setEditingTransactionIndex(null);
      } else {
        fieldArray.append({
          ...data,
          index: fieldArray.fields.length,
        });
      }
    },
    [editingTransactionIndex, fieldArray],
  );

  const handleSubmit = useCallback(() => {
    setAddingTransaction(true);
  }, []);

  const handleTransactionEdit = useCallback((index: number) => {
    setEditingTransactionIndex(index);
    setAddingTransaction(true);
  }, []);

  const handleTransactionDelete = useCallback(
    (index: number) => {
      fieldArray.remove(index);
    },
    [fieldArray.remove],
  );

  const handleFinalSubmit = useCallback(() => {
    const cleanData = {
      ...formControl.getValues(),
      transactions: formControl.getValues().transactions?.map(({ id: _, ...rest }) => rest),
    };
    disputeTransactionsStore.post(encodeRequest(cleanData as IForm & ChangeCardNameRequest));
  }, [disputeTransactionsStore, formControl]);
  const handleCloseConfirmation = useCallback(() => {
    setConfirmationVisible(false);
    formControl.reset({
      firstName: '',
      lastName: '',
      cardNumber: '',
      transactions: [],
    });
    disputeTransactionsStore.clear();
  }, [formControl, disputeTransactionsStore]);

  return (
    <PageLayout>
      <div className='flex flex-col items-center'>
        <div
          className='mb-5 flex w-full items-end justify-between bg-[#fafafa] bg-contain bg-no-repeat lg:mb-10 lg:!bg-none lg:!pt-0'
          style={{ paddingTop: 'calc(100% - 50px)', backgroundPosition: '0 -20%', backgroundImage: `url(${bgImg})` }}
        >
          <form
            className='relative m-0 flex h-[640px] w-full flex-col items-center justify-center bg-[100%] bg-no-repeat px-4 max-lg:!ml-0 max-lg:!bg-none lg:mt-12 lg:items-start lg:justify-items-end'
            style={{ marginLeft: 'calc((100vw - 1082px) / 2)', backgroundImage: `url(${formBgImg})` }}
            onSubmit={formControl.handleSubmit(handleSubmit)}
          >
            <h1 className='m-0 mb-[72px] text-center font-[Prata] text-lg text-[#272727] lg:mb-12 lg:max-w-[500px] lg:text-left lg:text-[56px]'>
              Declaration of Disputed Charges
            </h1>
            <div className='mb-12 flex max-w-[333px] flex-col justify-stretch gap-6 rounded-lg bg-[#fff] p-4' style={{ width: 'calc(100% - 32px)' }}>
              <Controller
                name='firstName'
                control={formControl.control}
                rules={{
                  maxLength: 100,
                }}
                render={({ field: { value, onChange } }) => (
                  <Input
                    labelClassName='!text-[#4c4c4c80]'
                    inputClassName='!h-[50px] !border-[#4c4c4c1a] !bg-[#fafafa]'
                    label='First Name'
                    name='first'
                    required
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              <Controller
                name='lastName'
                control={formControl.control}
                rules={{
                  maxLength: 100,
                }}
                render={({ field: { value, onChange } }) => (
                  <Input
                    labelClassName='!text-[#4c4c4c80]'
                    inputClassName='!h-[50px] !border-[#4c4c4c1a] !bg-[#fafafa]'
                    label='Last Name'
                    name='last'
                    required
                    value={value}
                    onChange={onChange}
                  />
                )}
              />
              <Controller
                name='cardNumber'
                control={formControl.control}
                rules={{
                  maxLength: 100,
                }}
                render={({ field: { value, onChange } }) => (
                  <Input
                    labelClassName='!text-[#4c4c4c80]'
                    inputClassName='font-inherit mt-1 block h-[50px] w-full rounded-lg border border-[#4c4c4c1a] bg-[#fafafa] px-4 py-3 text-[#0f0f11]'
                    name='cardNumber'
                    required
                    value={value}
                    onChange={onChange}
                    label='Full 16 digit card number'
                    cardFormat
                    type='number'
                    inputmode='numeric'
                  />
                )}
              />
            </div>
            <Button type='submit' className='w-full lg:w-[333px]'>
              Add Transaction Description
            </Button>
          </form>
        </div>
        <TransactionForm
          addingTransaction={addingTransaction}
          setAddingTransaction={setAddingTransaction}
          fieldArray={fieldArray}
          initialData={editingTransactionIndex !== null ? fieldArray.fields[editingTransactionIndex] : null}
          editingTransactionIndex={editingTransactionIndex}
          onSave={handleSaveTransaction}
        />
        <div
          className={cn(
            'w-[calc(100%-40px)] max-w-[1122px] pb-12 lg:-mr-12 lg:-mb-12 lg:flex lg:flex-wrap',
            transactions ? transactions.length >= 3 && 'lg:!-mr-10 lg:!-mb-10' : '',
          )}
        >
          {transactions
            ? transactions.map((item, i) => (
                <TransactionTile
                  className='!mr-12 !mb-12 max-lg:!mb-0 max-lg:!w-full'
                  date={item.date}
                  amount={item.amount}
                  currency={item.formCurrency === 'Other' ? item.currency : item.formCurrency}
                  merchant={item.merchant}
                  typeOfDispute={item.formTypeOfDispute === 'Other' ? item.typeOfDispute : item.formTypeOfDispute}
                  natureOfDispute={item.natureOfDispute}
                  onEdit={() => {
                    handleTransactionEdit(i);
                  }}
                  onDelete={() => handleTransactionDelete(i)}
                  key={i}
                  index={i}
                />
              ))
            : null}
        </div>
        {transactions && transactions.length > 0 ? (
          <div className='w-[1082px] max-w-[1122px] pt-10 pb-12 max-lg:w-[calc(100%-40px)]'>
            <Button
              type='submit'
              className='flex h-[50px] w-full max-w-[333px] cursor-pointer items-center justify-center rounded-lg border border-transparent bg-[#272727] text-center font-semibold text-[#d7c29a] max-lg:max-w-full'
              onClick={handleFinalSubmit}
            >
              Submit
            </Button>
          </div>
        ) : null}
        <Modal isOpen={confirmationVisible} onClose={handleCloseConfirmation}>
          <p className='max-md:text-[#4c4c4c80]'>Your declaration has been successfully submitted</p>
          <Button className='max-w:w-full mx-auto mt-8 w-[200px]' onClick={handleCloseConfirmation}>
            Close
          </Button>
        </Modal>
      </div>
    </PageLayout>
  );
};
